/**
 * ROAD CAPTIONS for the drive (B4, spec §E.6). Pure.
 *
 *   captionAt(spans, m)   the road name at m metres along the route, or null in an unnamed gap. The drive
 *                          emits this raw value on bus "drive:progress"; the HUD (B8) applies the hold
 *                          (2500 ms minimum) and skips stretches shorter than that.
 *   turnList(spans, L)    the reduced-motion equivalent: one row per named road with its km, merged when
 *                          consecutive spans carry the same name.
 */
import type { NameSpan } from "@/contracts/data";

/** Binary search over spans sorted by fromM (the data's order). Spans may have gaps. */
export function spanIndexAt(spans: readonly NameSpan[], m: number): number {
  let lo = 0;
  let hi = spans.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const s = spans[mid];
    if (m < s.fromM) hi = mid - 1;
    else if (m >= s.toM) lo = mid + 1;
    else return mid;
  }
  // the very end of the route belongs to the last span that reaches it
  const last = spans[spans.length - 1];
  if (last && m >= last.toM && m - last.toM < 1e-6) return spans.length - 1;
  return -1;
}

export function captionAt(spans: readonly NameSpan[] | null | undefined, m: number): string | null {
  if (!spans?.length || !Number.isFinite(m)) return null;
  const i = spanIndexAt(spans, m);
  return i < 0 ? null : spans[i].name;
}

export interface TurnRow {
  name: string;
  ref?: string;
  fromM: number;
  toM: number;
  km: number; // 1 dp
}

export function turnList(spans: readonly NameSpan[] | null | undefined): TurnRow[] {
  const out: TurnRow[] = [];
  for (const s of spans ?? []) {
    const prev = out[out.length - 1];
    if (prev && prev.name === s.name) {
      prev.toM = s.toM;
      prev.km = Math.round((prev.toM - prev.fromM) / 100) / 10;
      continue;
    }
    out.push({ name: s.name, ...(s.ref ? { ref: s.ref } : {}), fromM: s.fromM, toM: s.toM, km: Math.round((s.toM - s.fromM) / 100) / 10 });
  }
  return out;
}
